import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import ComputerIcon from '@mui/icons-material/Computer';
import type { FC, ReactNode } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';
import { SecurityTimelineListItemShell, securityDashboardCardSx } from './securityDashboardCard';
import { EDR_COLORS, edrMonoStyle } from './edrTokens';
import { EdrStatusPill } from './EdrStatusPill';
import type { EdrStatusTone } from './edrTokens';

/** Section card matching the detail mock: white card, header row with red icon + title. */
export const EdrDetailSectionCard: FC<{
  icon: ReactNode;
  title: string;
  children: ReactNode;
  /** When false, body has no outer padding (e.g. timeline items). Default true. */
  bodyPadding?: boolean;
}> = ({ icon, title, children, bodyPadding = true }) => (
  <Card sx={{ ...securityDashboardCardSx, overflow: 'hidden' }}>
    <Box
      display="flex"
      alignItems="center"
      style={{
        gap: 8,
        padding: '10px 14px',
        borderBottom: `1px solid ${EDR_COLORS.slate200}`,
        background: 'rgba(248, 250, 252, 0.5)',
      }}
    >
      <Box display="flex" alignItems="center" style={{ color: EDR_COLORS.critical, lineHeight: 0 }}>
        {icon}
      </Box>
      <Typography
        style={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: 700,
          fontSize: 13,
          lineHeight: '18px',
          color: EDR_COLORS.textHeading,
        }}
      >
        {title}
      </Typography>
    </Box>
    <Box style={bodyPadding ? { padding: '12px 14px' } : undefined}>{children}</Box>
  </Card>
);

/** Clamped body text with View more / View less when content overflows. */
export const EdrExpandableText: FC<{ text: string; lines?: number }> = ({ text, lines = 3 }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [canToggle, setCanToggle] = useState(false);

  useLayoutEffect(() => {
    if (expanded || !ref.current) return;
    setCanToggle(ref.current.scrollHeight > ref.current.clientHeight + 1);
  }, [text, expanded, lines]);

  return (
    <div>
      <div
        ref={ref}
        style={{
          fontSize: 12,
          color: EDR_COLORS.textBody,
          lineHeight: 1.6,
          wordBreak: 'break-word',
          ...(expanded
            ? { whiteSpace: 'pre-wrap' }
            : {
                display: '-webkit-box',
                WebkitLineClamp: lines,
                WebkitBoxOrient: 'vertical' as const,
                overflow: 'hidden',
              }),
        }}
      >
        {text}
      </div>
      {canToggle || expanded ? (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          style={{
            display: 'block',
            marginLeft: 'auto',
            marginTop: 6,
            border: 'none',
            background: 'none',
            padding: 0,
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 600,
            color: EDR_COLORS.brand600,
            textDecoration: 'underline',
          }}
        >
          {expanded ? 'View less' : 'View more'}
        </button>
      ) : null}
    </div>
  );
};

export type EdrDetailKeyValue = {
  label: string;
  value?: ReactNode;
  mono?: boolean;
};

const hasValue = (value: ReactNode | undefined): boolean =>
  value !== undefined && value !== null && value !== '';

export const EdrDetailKeyValueRows: FC<{ rows: EdrDetailKeyValue[] }> = ({ rows }) => {
  const visible = rows.filter((row) => hasValue(row.value));
  if (visible.length === 0) return null;

  return (
    <Box display="flex" flexDirection="column">
      {visible.map((row, index) => (
        <Box
          key={row.label}
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          style={{
            gap: 16,
            padding: '8px 0',
            borderBottom:
              index < visible.length - 1 ? `1px solid ${EDR_COLORS.borderSubtle}` : undefined,
          }}
        >
          <Typography
            component="div"
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 12,
              color: EDR_COLORS.textSecondary,
              flex: '0 1 38%',
              lineHeight: 1.45,
            }}
          >
            {row.label}
          </Typography>
          <Typography
            component="div"
            style={{
              fontFamily: row.mono ? edrMonoStyle.fontFamily : 'Inter, sans-serif',
              fontSize: 12,
              color: EDR_COLORS.textPrimary,
              fontWeight: 600,
              textAlign: 'right',
              flex: '1 1 0',
              minWidth: 0,
              wordBreak: row.mono ? 'break-all' : 'break-word',
              lineHeight: 1.45,
            }}
          >
            {row.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export type EdrDetailTimelineEvent = {
  id: string;
  title: string;
  subtitle?: string;
  time?: string;
  tone?: 'neutral' | 'critical';
};

export const EdrDetailTimeline: FC<{ events: EdrDetailTimelineEvent[] }> = ({ events }) => {
  if (events.length === 0) return null;

  return (
    <Box>
      {events.map((event, index) => (
        <SecurityTimelineListItemShell
          key={event.id}
          isLast={index === events.length - 1}
          dotColor={event.tone === 'neutral' ? EDR_COLORS.slate200 : EDR_COLORS.critical}
        >
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" style={{ gap: 12 }}>
            <Typography
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 12.5,
                fontWeight: 600,
                lineHeight: '18px',
                color: EDR_COLORS.textHeading,
              }}
            >
              {event.title}
            </Typography>
            <Typography
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 11,
                lineHeight: '16px',
                color: EDR_COLORS.textMuted,
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {event.time || '—'}
            </Typography>
          </Box>
          {event.subtitle ? (
            <Typography
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 11,
                lineHeight: '16px',
                color: EDR_COLORS.slate500,
                marginTop: 3,
              }}
            >
              {event.subtitle}
            </Typography>
          ) : null}
        </SecurityTimelineListItemShell>
      ))}
    </Box>
  );
};

export type EdrDetailEndpointSidebarProps = {
  endpoint: string;
  statusLabel?: string;
  statusTone?: EdrStatusTone;
  organization?: string;
  operatingSystem?: string;
};

export const EdrDetailEndpointSidebar: FC<EdrDetailEndpointSidebarProps> = ({
  endpoint,
  statusLabel,
  statusTone,
  organization,
  operatingSystem,
}) => (
  <EdrDetailSectionCard icon={<ComputerIcon style={{ fontSize: 16 }} />} title="Endpoint">
    <Box
      style={{
        padding: '10px 12px',
        borderRadius: 8,
        border: `1px solid ${EDR_COLORS.borderDefault}`,
        background: EDR_COLORS.slate50,
        marginBottom: 10,
      }}
    >
      <Box display="flex" alignItems="flex-start" style={{ gap: 10 }}>
        <ComputerIcon style={{ fontSize: 18, color: EDR_COLORS.critical, marginTop: 2 }} />
        <Box minWidth={0}>
          <Typography
            style={{
              fontSize: 12.5,
              fontWeight: 700,
              color: EDR_COLORS.textPrimary,
              fontFamily: edrMonoStyle.fontFamily,
              wordBreak: 'break-all',
              lineHeight: 1.35,
            }}
          >
            {endpoint || '—'}
          </Typography>
          {statusLabel ? (
            <Box mt={1}>
              <EdrStatusPill tone={statusTone ?? 'success'} label={statusLabel} />
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>

    <EdrDetailKeyValueRows
      rows={[
        { label: 'Organization', value: organization },
        { label: 'Operating System', value: operatingSystem },
      ]}
    />
  </EdrDetailSectionCard>
);
